import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";
import { Page } from "../../enums/core-enums";
import { AuthService } from "../../services/auth.service";
import { MessageService } from "../../services/message.service";
import { ThemeService } from "../../services/theme.service";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatMenuModule } from "@angular/material/menu";

import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DialogService } from "../../services/dialog.service";
import { environment } from "../../../environments/environment";

@Component({
    selector: "app-menu",
    templateUrl: "./menu.component.html",
    standalone: true,
    imports: [
    MatToolbarModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    RouterLinkActive
],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./menu.component.scss"]
})
export class MenuComponent implements OnInit, OnDestroy {
  isMobileMenuOpen = false;
  private routerSubscription?: Subscription;
  private userGroupsSubscription?: Subscription;
  currentPageName = 'Pendelportalen';
  userGroupsLoaded = false;

  get isDarkMode(): boolean {
    return this.themeService.isDark;
  }

  menuItems = [
    {
      displayName: "Översikt",
      path: "",
      page: Page.home,
      redirect: false,
      protected: false
    },
    {
      displayName: "Trafikinfo",
      path: "trafikinfo",
      page: Page.trafficInfo,
      redirect: false,
      protected: false
    },
    {
      displayName: "Telefonbok",
      path: "kontakter",
      page: Page.contacts,
      readWriteGroups: environment.readWriteGroups,
      redirect: false,
      protected: true
    },
    {
      displayName: "Uttagna tågordrar",
      path: "tagorder",
      page: Page.trainOrders,
      readWriteGroups: environment.trainOrderGroups,
      redirect: false,
      protected: true
    },
    {
      displayName: "Alkolås",
      path: "alcolock",
      page: Page.alcolock,
      redirect: false,
      protected: true,
      readWriteGroups: environment.alcolockGroups,
      children: [
        {
          displayName: "Röda Blåsningar",
          path: "alcolock/roda-blasningar",
          page: Page.alcolock,
          redirect: false,
          protected: true,
          readWriteGroups: environment.alcolockGroups
        },
        {
          displayName: "Turanmälningar",
          path: "alcolock/turanmalningar",
          page: Page.alcolock,
          redirect: false,
          protected: true,
          readWriteGroups: environment.alcolockGroups
        }
      ]
    },
    {
      displayName: "Föraravvikelser",
      path: "foraravvikelser",
      page: Page.driverDeviations,
      redirect: false,
      protected: true,
      readWriteGroups: environment.driverDeviationGroups
    },
    {
      displayName: "Tåg i Tid Admin",
      path: environment.trainOnTimeAdminUrl,
      page: Page.trainOnTimeAdmin,
      readWriteGroups: environment.readWriteGroups,
      redirect: true,
      protected: true
    }
  ];

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private router: Router,
    public themeService: ThemeService
  ) {}

  ngOnInit() {
    this.updateCurrentPageName();

    this.userGroupsSubscription = this.authService.userGroupsReady$.subscribe(
      loaded => {
        this.userGroupsLoaded = loaded;
      }
    );

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeMobileMenu();
        this.updateCurrentPageName();
      });
  }

  private updateCurrentPageName() {
    const currentPath = this.router.url.split('?')[0].replace(/^\//, '');
    for (const item of this.menuItems) {
      if (item.path === '' && currentPath === '') {
        this.currentPageName = item.displayName;
        return;
      }
      if (item.path !== '' && currentPath.startsWith(item.path)) {
        if (item.children) {
          const child = item.children.find((c: any) => c.path === currentPath);
          if (child) {
            this.currentPageName = child.displayName;
            return;
          }
        }
        this.currentPageName = item.displayName;
        return;
      }
    }
    this.currentPageName = 'Pendelportalen';
  }

  isChildRouteActive(menuItem: any): boolean {
    if (!menuItem.children) return false;
    const currentPath = this.router.url.split('?')[0];
    return menuItem.children.some((child: any) => currentPath === '/' + child.path);
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.userGroupsSubscription) {
      this.userGroupsSubscription.unsubscribe();
    }
    // Reset body scroll
    document.body.style.overflow = '';
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.updateBodyScroll();
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.updateBodyScroll();
  }

  private updateBodyScroll() {
    // Prevent body scroll when mobile menu is open
    if (this.isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  logout() {
    if (!this.messageService.hasChanges()) {
      this.authService.logout();
    } else {
      this.dialogService.unsavedChangesConfirmation().subscribe(result => {
        if (result) {
          this.authService.logout();
        }
      });
    }
  }

  user() {
    return this.authService.getUserName();
  }

  loggedIn() {
    return this.authService.loggedIn;
  }

  hasGroups(readWriteGroups: string[]) {
    return this.authService.hasReadWriteGroups(readWriteGroups);
  }

  shouldShow(menuItem: any) {
    if (!this.authService.loggedIn || !this.authService.getActiveAccount()) {
      return false;
    }

    // For non-protected items, show immediately
    if (!menuItem.protected) {
      return true;
    }

    // For protected items, wait for user groups to be loaded
    if (!this.userGroupsLoaded) {
      return false;
    }

    // Check specific access methods for alcolock and driver deviations
    if (menuItem.page === Page.alcolock) {
      return this.authService.hasAlcolockAccess();
    }
    if (menuItem.page === Page.driverDeviations) {
      return this.authService.hasDriverDeviationAccess();
    }
    
    // Check other protected items with readWriteGroups
    return this.hasGroups(menuItem.readWriteGroups);
  }
}
